import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AddCryptoBeneficiary from "./AddCryptoBeneficiary";
import BeneficiaryDetailsPanel from "./BeneficiaryDetailsPanel";
import BeneficiaryListPanel from "./BeneficiaryListPanel";
import { listCryptoBeneficiary } from "../../../redux/actions/CryptoTransaction/CryptoBeneficiary";
import CryptoBeneficiariesSkeleton from "../../../components/Skeleton/CryptoBeneficiariesSkeleton";
import EmptyBeneficiaries from "../../../components/Empty/EmptyCryptoBeneficiaries";
import TransferModal from "../TransferModal";
import { scrollToTopWithEase } from "../../../helper/ScrollHelper";

const Beneficiaries = ({ onPageBack }) => {
  const dispatch = useDispatch();
  const { client_id } = useSelector((state) => state.userInfo.userInfo);

  const [view, setView] = useState("list");
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [mobileShowDetails, setMobileShowDetails] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);

  // 1. Fetch live crypto accounts from Redux
  const cryptoAccounts = useSelector(
    (state) => state.client?.clientInfo?.crypto_accounts ?? []
  );

  // 2. Dynamically calculate total USD balance & total asset count
  const { totalBalanceUsd, isNewUser } = useMemo(() => {
    if (!cryptoAccounts || cryptoAccounts.length === 0) {
      return { totalBalanceUsd: 0, assetCount: 0, isNewUser: true };
    }

    const isNewUser = !cryptoAccounts?.some(
      (account) => account?.cryptoAddresses?.length > 0
    );
    const total = cryptoAccounts.reduce((sum, account) => {
      const balance = Number(account?.balance ?? 0);
      const unitPrice = Number(account?.unit_price_usd ?? 0);
      return sum + balance * unitPrice;
    }, 0);

    return {
      totalBalanceUsd: total,
      isNewUser,
    };
  }, [cryptoAccounts]);

  const fetchBeneficiaries = useCallback(async () => {
    if (!client_id) return;

    try {
      setLoading(true);
      setError("");

      const res = await dispatch(listCryptoBeneficiary(client_id));
      const data = Array.isArray(res) ? res : [];

      setBeneficiaries(data);

      if (!selectedId && data.length) {
        setSelectedId(data[0].id);
      }
    } catch (err) {
      setError(err?.message || "Failed to load beneficiaries.");
    } finally {
      setLoading(false);
    }
  }, [client_id, dispatch, selectedId]);

  useEffect(() => {
    fetchBeneficiaries();
    // eslint-disable-next-line
  }, []);

  const selectedBeneficiary = useMemo(
    () => beneficiaries.find((item) => item.id === selectedId) ?? null,
    [beneficiaries, selectedId]
  );

  // Find the associated account/asset for the selected beneficiary
  const selectedBeneficiaryAsset = useMemo(() => {
    if (!selectedBeneficiary || !cryptoAccounts.length) return null;

    return (
      cryptoAccounts.find((account) => {
        // Match by account_id / id
        if (
          selectedBeneficiary.account_id &&
          (account.id === selectedBeneficiary.account_id ||
            account.account_id === selectedBeneficiary.account_id)
        ) {
          return true;
        }

        // Fallback match by asset_id or currency_id if account_id is not directly matching
        if (
          selectedBeneficiary.asset_id &&
          account.asset_id === selectedBeneficiary.asset_id
        ) {
          return true;
        }

        if (selectedBeneficiary.currency_id && account.currency_id === selectedBeneficiary.currency_id) {
          return true;
        }

        return false;
      }) ?? null
    );
  }, [cryptoAccounts, selectedBeneficiary]);

  const filteredBeneficiaries = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) return beneficiaries;

    return beneficiaries.filter((beneficiary) => {
      return [
        beneficiary.label,
        beneficiary.beneficiary_first_name,
        beneficiary.beneficiary_last_name,
        beneficiary.beneficiary_legal_name,
        beneficiary.wallet_address,
        beneficiary.asset_id,
        beneficiary.beneficiary_vasp_name,
      ]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(query));
    });
  }, [beneficiaries, search]);

  const handleDesktopSelect = useCallback((beneficiary) => {
    setSelectedId(beneficiary.id);
    scrollToTopWithEase();
  }, []);

  const handleMobileSelect = useCallback((beneficiary) => {
    setSelectedId(beneficiary.id);
    setMobileShowDetails(true);
  }, []);

  const handleBeneficiaryAdded = useCallback(async () => {
    await fetchBeneficiaries();
    setView("list");
  }, [fetchBeneficiaries]);

  const listPanelProps = useMemo(() => ({
      beneficiaries: filteredBeneficiaries,
      selectedId,
      loading,
      error,
      search,
      setSearch,
      onAddBeneficiary: () => setView("add"),
    }),
    [filteredBeneficiaries, selectedId, loading, error, search]
  );

  if (loading) {
    return (
      <div
        className={`flex flex-1 flex-col bg-background-surface max-sm:rounded-none max-sm:shadow-none sm:rounded-[16px] sm:p-6 sm:shadow-[0px_4px_12px_rgba(0,0,0,0.08)] ${
          view === "add" ? "max-sm:px-4 max-sm:pb-2 max-sm:pt-4" : "max-sm:p-4"
        }`}
      >
        <CryptoBeneficiariesSkeleton />
      </div>
    );
  }

  if (!loading && !beneficiaries.length && view === "list") {
    return (
      <div className="flex flex-1 flex-col bg-background-surface max-sm:p-4 sm:rounded-[16px] sm:p-6 sm:shadow-[0px_4px_12px_rgba(0,0,0,0.08)]">
        <EmptyBeneficiaries onAddBeneficiary={() => setView("add")} />
      </div>
    );
  }

  return (
    <>
      <div
        className={`flex flex-1 flex-col bg-background-surface max-sm:rounded-none max-sm:shadow-none sm:rounded-[16px] sm:p-6 sm:shadow-[0px_4px_12px_rgba(0,0,0,0.08)] ${
          view === "add" ? "max-sm:px-4 max-sm:pb-2 max-sm:pt-4" : "max-sm:p-4"
        }`}
      >
        {view === "add" ? (
          <AddCryptoBeneficiary
            onBack={() => setView("list")}
            onContinue={handleBeneficiaryAdded}
          />
        ) : (
          <>
            {/* Desktop Layout */}
            <div className="hidden lg:flex lg:flex-row lg:items-start lg:gap-9">
              <BeneficiaryListPanel
                {...listPanelProps}
                onSelect={handleDesktopSelect}
              />

              <div
                className="w-px shrink-0 self-stretch bg-divider"
                aria-hidden
              />

              <BeneficiaryDetailsPanel
                beneficiary={selectedBeneficiary}
                onTransfer={() => setIsTransferModalOpen(true)}
                onClose={() => setMobileShowDetails(false)}
              />
            </div>

            {/* Mobile Layout */}
            <div className="sm:hidden block">
              <BeneficiaryListPanel
                {...listPanelProps}
                isMobile
                onPageBack={onPageBack}
                onSelect={handleMobileSelect}
              />

              <BeneficiaryDetailsPanel
                isOpen={mobileShowDetails}
                beneficiary={selectedBeneficiary}
                onTransfer={() => setIsTransferModalOpen(true)}
                onClose={() => {
                  setSelectedId(null);
                  setMobileShowDetails(false);
                }}
              />
            </div>
          </>
        )}
      </div>

      <TransferModal
        isOpen={isTransferModalOpen}
        isNewUser={isNewUser}
        totalBalanceUsd={totalBalanceUsd}
        defaultTab={totalBalanceUsd > 0 ? "Withdraw" : "Deposit"}
        beneficiary={selectedBeneficiary}
        beneficiaryAsset={selectedBeneficiaryAsset}
        defaultStep={2}
        onClose={() => setIsTransferModalOpen(false)}
        onGuideBuyCrypto={() => setIsTransferModalOpen(false)}
      />
    </>
  );
};

export default Beneficiaries;